import React, { useState, useEffect, useRef } from "react";
import {
    Stack,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    Box,
    Button,
    Typography,
    Avatar,
    FormHelperText
} from "@mui/material";
import { CloudUpload, Person, Delete } from "@mui/icons-material";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import apiService from '@services/common'
import { useGlobalContext } from '@providers/globalProvider';

const EditDrawer = ({ user, submitFunction = () => { }, onClose }) => {
    const [roles, setRoles] = useState([])
    const [avatarPreview, setAvatarPreview] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const [currentAvatar, setCurrentAvatar] = useState(null)
    const fileInputRef = useRef(null)

    const { showNotification, showLoading, hideLoading } = useGlobalContext()

    useEffect(() => {
        showLoading()
        apiService.get('role')
            .then(res => {
                setRoles(res.data.data)
                hideLoading()
            })
            .catch(err => {
                console.log(err)
                hideLoading()
            })
    }, [])

    // Load user data khi component mount hoặc user thay đổi
    useEffect(() => {
        if (user) {
            // Set current avatar
            if (user.avatar) {
                setCurrentAvatar(process.env.REACT_APP_BACKEND_URL + user.avatar)
                setAvatarPreview(process.env.REACT_APP_BACKEND_URL + user.avatar)
            }

            // Set giá trị mặc định cho form
            setValue('name', user.name || '')
            setValue('email', user.email || '')

            // Set role (lấy role đầu tiên nếu có)
            if (user.roles && user.roles.length > 0) {
                setValue('role', user.roles[0].name || '')
            }
        }
    }, [user])

    const schema = yup.object({
        name: yup.string().required('Vui lòng nhập tên'),
        email: yup.string().email('Email không hợp lệ').required('Vui lòng nhập email'),
        role: yup.string(),
    });

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: '',
            email: '',
            role: ''
        }
    });

    // Xử lý khi chọn file
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Kiểm tra loại file
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            showNotification('error', 'Chỉ chấp nhận file ảnh (JPEG, PNG, JPG, GIF, WebP)');
            return;
        }

        // Kiểm tra kích thước (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            showNotification('error', 'Kích thước ảnh tối đa là 2MB');
            return;
        }

        setSelectedFile(file);

        // Tạo preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setAvatarPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Xóa ảnh đã chọn
    const handleRemoveAvatar = () => {
        setSelectedFile(null);
        setAvatarPreview(currentAvatar); // Quay về ảnh cũ
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Xóa ảnh hiện tại (đánh dấu xóa trên server)
    const handleRemoveCurrentAvatar = () => {
        setCurrentAvatar(null);
        setAvatarPreview(null);
        setSelectedFile({ remove: true }); // Đánh dấu xóa
    };

    // Kích hoạt file input
    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const onSubmit = (data) => {
        // Tạo FormData để gửi cả file
        const formData = new FormData();

        // 🔥 THÊM ID USER VÀO FORMDATA
        formData.append('id', user.id);

        // Thêm các field text
        Object.keys(data).forEach(key => {
            if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
                formData.append(key, data[key]);
            }
        });

        // Thêm file mới nếu có
        if (selectedFile instanceof File) {
            formData.append('avatar', selectedFile);
        }
        // Đánh dấu xóa ảnh nếu người dùng muốn xóa
        else if (selectedFile?.remove === true) {
            formData.append('remove_avatar', 'true');
        }

        // Debug: log FormData
        console.log('=== FormData gửi đi (Edit) ===');
        for (let pair of formData.entries()) {
            console.log(pair[0] + ':', pair[1]);
        }

        // Gọi submitFunction với FormData
        submitFunction(formData);
    };

    const handleResetForm = () => {
        reset();
        setAvatarPreview(currentAvatar);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Box>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Chỉnh sửa người dùng: {user?.name}
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                    <Box sx={{ maxWidth: 400 }}>
                        {/* Upload Avatar */}
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />

                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    cursor: 'pointer'
                                }}
                                onClick={handleAvatarClick}
                            >
                                <Avatar
                                    src={avatarPreview}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        border: '2px dashed #ddd',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            opacity: 0.8
                                        }
                                    }}
                                >
                                    <Person sx={{ fontSize: 48, color: '#999' }} />
                                </Avatar>

                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        borderRadius: '50%',
                                        p: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CloudUpload fontSize="small" />
                                </Box>
                            </Box>

                            <Box sx={{ mt: 1 }}>
                                {(avatarPreview || selectedFile) && (
                                    <Button
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={handleRemoveAvatar}
                                        sx={{ mr: 1 }}
                                        color="error"
                                    >
                                        {selectedFile ? 'Hủy ảnh mới' : 'Xóa ảnh'}
                                    </Button>
                                )}

                                {currentAvatar && !selectedFile && (
                                    <Button
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={handleRemoveCurrentAvatar}
                                        color="warning"
                                    >
                                        Xóa ảnh hiện tại
                                    </Button>
                                )}
                            </Box>

                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Click để thay đổi ảnh đại diện (JPEG, PNG, GIF, WebP, max 2MB)
                            </Typography>
                        </Box>

                        {/* Tên */}
                        <TextField
                            label="Tên"
                            fullWidth
                            size="small"
                            {...register('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                        />

                        {/* Email */}
                        <TextField
                            label="Email"
                            fullWidth
                            size="small"
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            sx={{ mt: 2 }}
                        />

                        {/* Role */}
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                                    <InputLabel id="role-select-label">Chọn nhóm quyền</InputLabel>
                                    <Select
                                        labelId="role-select-label"
                                        value={field.value}
                                        label="Chọn nhóm quyền"
                                        onChange={field.onChange}
                                    >
                                        <MenuItem value="">Không chọn</MenuItem>
                                        {roles.map((item, index) => (
                                            <MenuItem key={index} value={item.name}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.role && (
                                        <FormHelperText error>{errors.role.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Box>

                    {/* Nút submit */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClose}
                        >
                            Hủy
                        </Button>

                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleResetForm}
                            >
                                Đặt lại
                            </Button>

                            <Button
                                type="submit"
                                variant="contained"
                                size="small"
                            >
                                Cập nhật
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </form>
        </Box>
    );
};

export default EditDrawer;